import type {
  FederatedLoginOptions,
  LoginIdMembers,
  LoginOptions,
  ScreenMembersOnLoginId,
  TransactionMembersOnLoginId,
} from '@auth0/auth0-acul-react/types';
import {
  useLoginId,
  useScreen,
  useTransaction,
} from '@auth0/auth0-acul-react/login-id';

import { executeSafely } from '@/utils/helpers/executeSafely';

export const useLoginIdManager = () => {
  const loginId: LoginIdMembers = useLoginId();
  const screen: ScreenMembersOnLoginId = useScreen();
  const transaction: TransactionMembersOnLoginId = useTransaction();

  const { isCaptchaAvailable, texts, captcha, signupLink } = screen;

  const {
    isSignupEnabled,
    isPasskeyEnabled,
    showPasskeyAutofill,
    alternateConnections,
  } = transaction;

  const handleLoginId = async (payload: LoginOptions): Promise<void> => {
    // Clean and prepare data
    const options: LoginOptions = {
      username: payload.username.trim(),
    };

    if (screen.isCaptchaAvailable && payload?.captcha?.trim()) {
      options.captcha = payload?.captcha.trim();
    }

    const logOptions = {
      ...options,
    };

    await executeSafely(
      `Perform Login operation with options: ${JSON.stringify(logOptions)}`,
      () => loginId.login(options),
    );
  };

  const handleFederatedLogin = async (payload: FederatedLoginOptions) => {
    await executeSafely(
      `Perform Federated login with connection: ${payload.connection}`,
      () => loginId.federatedLogin(payload),
    );
  };

  const handlePasskeyLogin = async () => {
    if (isPasskeyEnabled) {
      await executeSafely(`Perform passkey login`, () =>
        loginId.passkeyLogin(),
      );
    }
  };

  const handleSignup = async () => {
    if (signupLink) {
      window.location.href = signupLink;
    }
  };

  return {
    loginId,
    handleLoginId,
    handleFederatedLogin,
    handlePasskeyLogin,
    handleSignup,
    texts,
    isSignupEnabled,
    isPasskeyEnabled,
    showPasskeyAutofill,
    isCaptchaAvailable,
    captcha,
    alternateConnections,
    signupLink,
  };
};
